import * as React from "react";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";

import { HotelImageCardProps } from "components/flexComponents/Hotels/typings";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";
import { useLocalization } from "@shared-ui/localization-context";

const HotelCard = (props: HotelImageCardProps) => {
  const { context, model, hotel } = props;
  const { callToAction, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const { formatText } = useLocalization();

  return (
    <UitkCard padded>
      <UitkLayoutFlex space="three">
        <UitkLayoutFlexItem minWidth={100} shrink={0}>
          <div>
            <UitkFigure ratio={UitkFigureAspectRatioType.R1_1} className="uitk-card-roundcorner-all">
              <LazyLoadedImage src={hotelImageUrl} alt={hotel.hotelName} context={context} />
            </UitkFigure>
          </div>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem grow={1}>
          <div>
            <UitkCardContentSection padded={false}>
              <UitkLayoutFlex direction="column">
                <UitkLayoutFlex justifyContent="space-between" className="all-b-margin-three">
                  <UitkLayoutFlex direction="column">
                    <UitkHeading tag="h3" size={5}>
                      {hotelName}
                    </UitkHeading>
                    <UitkRating
                      rating={hotelStarRating}
                      icon={ratingIcon(context.locale)}
                      className={context.site.brand}
                    />
                    <UitkText size={300}>{hotelFormattedFullAddress}</UitkText>
                  </UitkLayoutFlex>
                  <UitkLayoutFlex>
                    <PriceLockup
                      hotel={hotel}
                      className="uitk-type-right"
                      priceA11y={new HotelPriceLockup(hotel, formatText)}
                      hasGetRatesText
                    />
                  </UitkLayoutFlex>
                </UitkLayoutFlex>
                <ValueProposition hotel={hotel} className="all-b-margin-three" />
                <DistanceOrRating model={model} hotel={hotel} className="all-b-margin-three" context={context} />
              </UitkLayoutFlex>
            </UitkCardContentSection>
            <UitkCardContentSection padded={false} border="top">
              <DescriptionOrReview model={model} hotel={hotel} className="all-t-margin-three" />
              <AmenitiesOrRecommendation model={model} hotel={hotel} className="all-t-margin-three" />
            </UitkCardContentSection>
          </div>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>

      <UitkCardLink>
        <TrackedLink
          data-testid={`hotelcard-link-${hotelName}`}
          aria-label={hotelName}
          moduleName="hotels"
          rfrr="card"
          rel={getHotelLinkRel(callToAction)}
          href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
        />
      </UitkCardLink>
    </UitkCard>
  );
};

export default HotelCard;
